import { useEffect, useState } from 'react';
import {
  collection, onSnapshot, addDoc, updateDoc, deleteDoc,
  doc, serverTimestamp, query, orderBy,
} from 'firebase/firestore';
import { db } from '../../firebase';

// Preset categories, shared with the itinerary builder's location picker.
export const LOCATION_CATEGORIES = [
  'Hotel',
  'Stadium',
  'Airport',
  'Training ground',
  'Restaurant',
  'Meeting point',
  'Attraction',
  'Other',
];

const emptyForm = {
  name: '',
  category: '',
  address: '',
  googleLink: '',
  notes: '',
};

export default function Locations() {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [deletingId, setDeletingId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(emptyForm);
  const [search, setSearch] = useState('');

  useEffect(() => {
    const q = query(collection(db, 'locations'), orderBy('name', 'asc'));
    const unsub = onSnapshot(q, (snap) => {
      setLocations(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
    return unsub;
  }, []);

  function handleChange(e) {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  }

  async function handleSave(e) {
    e.preventDefault();
    setError('');
    if (!form.name.trim()) {
      setError('Name is required.');
      return;
    }
    setSaving(true);
    try {
      await addDoc(collection(db, 'locations'), {
        name: form.name.trim(),
        category: form.category,
        address: form.address.trim(),
        googleLink: form.googleLink.trim(),
        notes: form.notes.trim(),
        createdAt: serverTimestamp(),
      });
      setForm(emptyForm);
      setShowForm(false);
    } catch (err) {
      console.error(err);
      setError('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  function startEdit(location) {
    setEditingId(location.id);
    setEditForm({
      name: location.name || '',
      category: location.category || '',
      address: location.address || '',
      googleLink: location.googleLink || '',
      notes: location.notes || '',
    });
  }

  async function handleUpdate(e) {
    e.preventDefault();
    if (!editForm.name.trim()) { setError('Name is required.'); return; }
    setError('');
    setSaving(true);
    try {
      await updateDoc(doc(db, 'locations', editingId), {
        name: editForm.name.trim(),
        category: editForm.category,
        address: editForm.address.trim(),
        googleLink: editForm.googleLink.trim(),
        notes: editForm.notes.trim(),
      });
      setEditingId(null);
    } catch (err) {
      console.error(err);
      setError('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id) {
    if (!window.confirm('Remove this location from the library?')) return;
    setDeletingId(id);
    try {
      await deleteDoc(doc(db, 'locations', id));
    } finally {
      setDeletingId(null);
    }
  }

  const filtered = search
    ? locations.filter((l) =>
        [l.name, l.address, l.category].filter(Boolean).some((v) =>
          v.toLowerCase().includes(search.toLowerCase())
        ))
    : locations;

  return (
    <div className="space-y-5">
      {/* ── Page header ── */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-[23px] font-extrabold text-navy tracking-[-0.4px]">Locations</h1>
          <p className="text-[13px] text-[#6B7280] mt-1">
            {locations.length > 0
              ? `${locations.length} saved location${locations.length !== 1 ? 's' : ''} — pick these when building itineraries`
              : 'Your library of reusable places (hotels, stadiums, pick-up points) with address & map link.'}
          </p>
        </div>
        {!showForm && (
          <button
            onClick={() => setShowForm(true)}
            className="flex items-center gap-1.5 bg-pb hover:bg-navy text-white text-[13px] font-bold px-[16px] py-[10px] rounded-[9px] transition shadow-sm whitespace-nowrap"
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
              <path d="M12 5v14M5 12h14" />
            </svg>
            Add location
          </button>
        )}
      </div>

      {/* Add form */}
      {showForm && (
        <div className="bg-white rounded-[14px] border border-[#E5E7EB] shadow-[0_1px_2px_rgba(16,24,40,.04)] p-[18px]">
          <h3 className="text-[14.5px] font-bold text-[#111827] mb-5">New Location</h3>
          <form onSubmit={handleSave} className="space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-[13px] font-medium text-[#374151] mb-1">
                  Name <span className="text-dr">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  placeholder="e.g. Vistamar Hotel reception"
                  className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-pb"
                />
              </div>
              <div>
                <label className="block text-[13px] font-medium text-[#374151] mb-1">Category</label>
                <select
                  name="category"
                  value={form.category}
                  onChange={handleChange}
                  className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-pb bg-white"
                >
                  <option value="">Select a category…</option>
                  {LOCATION_CATEGORIES.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="block text-[13px] font-medium text-[#374151] mb-1">Address</label>
                <input
                  type="text"
                  name="address"
                  value={form.address}
                  onChange={handleChange}
                  placeholder="e.g. Av. de Neptuno 32, 03016 Alicante, Spain"
                  className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-pb"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-[13px] font-medium text-[#374151] mb-1">Google Maps link</label>
                <input
                  type="url"
                  name="googleLink"
                  value={form.googleLink}
                  onChange={handleChange}
                  placeholder="https://maps.google.com/..."
                  className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-pb"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-[13px] font-medium text-[#374151] mb-1">Notes</label>
                <input
                  type="text"
                  name="notes"
                  value={form.notes}
                  onChange={handleChange}
                  placeholder="Optional"
                  className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-pb"
                />
              </div>
            </div>

            {error && <p className="text-[12px] text-dr">{error}</p>}

            <div className="flex gap-2">
              <button type="submit" disabled={saving}
                className="px-[18px] py-[10px] text-[13px] font-bold text-white bg-pb hover:bg-navy rounded-[9px] transition disabled:opacity-60">
                {saving ? 'Saving…' : 'Save location'}
              </button>
              <button type="button" onClick={() => { setShowForm(false); setForm(emptyForm); setError(''); }}
                className="px-[18px] py-[10px] text-[13px] font-semibold text-[#6B7280] hover:text-[#374151] transition">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Search */}
      {!loading && locations.length > 0 && (
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search locations by name, address or category…"
          className="w-full sm:max-w-[360px] border border-[#E5E7EB] rounded-[9px] px-3 py-[9px] text-[13px] focus:outline-none focus:ring-2 focus:ring-pb"
        />
      )}

      {/* Locations list */}
      {loading ? (
        <div className="bg-white rounded-[14px] border border-[#E5E7EB] p-8 text-center">
          <p className="text-[13px] text-[#9CA3AF]">Loading…</p>
        </div>
      ) : locations.length === 0 ? (
        <div className="bg-white rounded-[14px] border border-[#E5E7EB] shadow-[0_1px_2px_rgba(16,24,40,.04)] p-12 text-center">
          <p className="text-[14px] font-bold text-[#111827] mb-1">No locations yet</p>
          <p className="text-[13px] text-[#9CA3AF]">Add one above to start building your library.</p>
        </div>
      ) : (
        <div className="bg-white rounded-[14px] border border-[#E5E7EB] shadow-[0_1px_2px_rgba(16,24,40,.04)] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px]">
              <thead>
                <tr className="bg-surface border-b border-[#E5E7EB]">
                  {['Location', 'Category', 'Address', 'Notes', ''].map((h, i) => (
                    <th key={i} className="px-[18px] py-[10px] text-left text-[10.5px] font-extrabold text-[#9CA3AF] uppercase tracking-[0.5px]">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((l) => (
                  <>
                    <tr key={l.id} className="border-b border-[#F1F3F6] hover:bg-surface transition-colors">
                      <td className="px-[18px] py-[12px]">
                        <p className="font-bold text-[13px] text-[#111827]">{l.name}</p>
                      </td>
                      <td className="px-[18px] py-[12px]">
                        {l.category
                          ? <span className="text-[11px] font-semibold bg-blue-light text-pb px-[8px] py-[3px] rounded-[20px]">{l.category}</span>
                          : <span className="text-[#D1D5DB]">—</span>}
                      </td>
                      <td className="px-[18px] py-[12px]">
                        {l.address ? <p className="text-[12px] text-[#374151]">{l.address}</p> : <span className="text-[#D1D5DB]">—</span>}
                        {l.googleLink && (
                          <a href={l.googleLink} target="_blank" rel="noopener noreferrer"
                            className="text-[11px] font-semibold text-pb hover:text-navy transition">Maps ↗</a>
                        )}
                      </td>
                      <td className="px-[18px] py-[12px] text-[#6B7280] text-[12.5px] max-w-[180px]">
                        {l.notes || <span className="text-[#D1D5DB]">—</span>}
                      </td>
                      <td className="px-[18px] py-[12px] text-right">
                        <div className="flex items-center justify-end gap-3">
                          <button
                            onClick={() => editingId === l.id ? setEditingId(null) : startEdit(l)}
                            className="text-[12.5px] font-bold text-pb hover:text-navy transition"
                          >
                            {editingId === l.id ? 'Close' : 'Edit'}
                          </button>
                          <button
                            onClick={() => handleDelete(l.id)}
                            disabled={deletingId === l.id}
                            className="text-[12.5px] font-bold text-dr/60 hover:text-dr transition disabled:opacity-50"
                          >
                            Remove
                          </button>
                        </div>
                      </td>
                    </tr>

                    {editingId === l.id && (
                      <tr key={`${l.id}-edit`} className="bg-surface border-b border-[#F1F3F6]">
                        <td colSpan={5} className="px-[18px] py-5">
                          <form onSubmit={handleUpdate} className="space-y-5">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                              <div>
                                <label className="block text-[11px] font-extrabold text-[#9CA3AF] uppercase tracking-[0.7px] mb-[6px]">
                                  Name <span className="text-dr">*</span>
                                </label>
                                <input
                                  type="text"
                                  value={editForm.name}
                                  onChange={(e) => setEditForm((p) => ({ ...p, name: e.target.value }))}
                                  className="w-full border border-[#E5E7EB] rounded-[9px] px-3 py-[9px] text-[13px] focus:outline-none focus:ring-2 focus:ring-pb"
                                />
                              </div>
                              <div>
                                <label className="block text-[11px] font-extrabold text-[#9CA3AF] uppercase tracking-[0.7px] mb-[6px]">Category</label>
                                <select
                                  value={editForm.category}
                                  onChange={(e) => setEditForm((p) => ({ ...p, category: e.target.value }))}
                                  className="w-full border border-[#E5E7EB] rounded-[9px] px-3 py-[9px] text-[13px] focus:outline-none focus:ring-2 focus:ring-pb bg-white"
                                >
                                  <option value="">Select a category…</option>
                                  {LOCATION_CATEGORIES.map((c) => (
                                    <option key={c} value={c}>{c}</option>
                                  ))}
                                </select>
                              </div>
                              <div className="sm:col-span-2">
                                <label className="block text-[11px] font-extrabold text-[#9CA3AF] uppercase tracking-[0.7px] mb-[6px]">Address</label>
                                <input
                                  type="text"
                                  value={editForm.address}
                                  onChange={(e) => setEditForm((p) => ({ ...p, address: e.target.value }))}
                                  placeholder="e.g. Av. de Neptuno 32, 03016 Alicante, Spain"
                                  className="w-full border border-[#E5E7EB] rounded-[9px] px-3 py-[9px] text-[13px] focus:outline-none focus:ring-2 focus:ring-pb"
                                />
                              </div>
                              <div className="sm:col-span-2">
                                <label className="block text-[11px] font-extrabold text-[#9CA3AF] uppercase tracking-[0.7px] mb-[6px]">Google Maps link</label>
                                <input
                                  type="url"
                                  value={editForm.googleLink}
                                  onChange={(e) => setEditForm((p) => ({ ...p, googleLink: e.target.value }))}
                                  placeholder="https://maps.google.com/..."
                                  className="w-full border border-[#E5E7EB] rounded-[9px] px-3 py-[9px] text-[13px] focus:outline-none focus:ring-2 focus:ring-pb"
                                />
                              </div>
                              <div className="sm:col-span-2">
                                <label className="block text-[11px] font-extrabold text-[#9CA3AF] uppercase tracking-[0.7px] mb-[6px]">Notes</label>
                                <input
                                  type="text"
                                  value={editForm.notes}
                                  onChange={(e) => setEditForm((p) => ({ ...p, notes: e.target.value }))}
                                  placeholder="Optional"
                                  className="w-full border border-[#E5E7EB] rounded-[9px] px-3 py-[9px] text-[13px] focus:outline-none focus:ring-2 focus:ring-pb"
                                />
                              </div>
                            </div>

                            {error && <p className="text-[12px] text-dr">{error}</p>}
                            <div className="flex gap-2">
                              <button type="submit" disabled={saving}
                                className="px-[18px] py-[10px] text-[13px] font-bold text-white bg-pb hover:bg-navy rounded-[9px] transition disabled:opacity-60">
                                {saving ? 'Saving…' : 'Save changes'}
                              </button>
                              <button type="button" onClick={() => setEditingId(null)}
                                className="px-4 py-[10px] text-[13px] font-semibold text-[#6B7280] hover:text-[#374151] transition">
                                Cancel
                              </button>
                            </div>
                          </form>
                        </td>
                      </tr>
                    )}
                  </>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
